import { inject, injectable } from 'tsyringe';
import type { CostForecastSnapshot } from '@bmai/insights-api-client';
import { BaseViewModel } from '@/shared/viewmodels/BaseViewModel';
import { IAnalyticsGatewayToken, type IAnalyticsGateway } from '@/shared/analytics/AnalyticsGateway';
import { LicensesApiService } from '@/features/licenses/LicensesApiService';
import { GlobalFilterValueService } from '@/features/global-filters/GlobalFilterValueService';
import { isSubscriptionCost } from '@/features/licenses/CostRecordDimensions';
import { formatPercent, formatUsd } from '@/shared/analytics/MetricFormatting';
import type { TopicCallout } from '@/shared/surfaces/TopicNarration';
import { CostForecastGateway } from './CostForecastGateway';
import { buildMonthlyComparison, computeForecast, type ForecastResult, type MonthComparison } from './CostForecast';
import { footingRecommendations, routingRecommendations, type Recommendation, type FootingInput, type ModelRoutingInput } from './CostRecommendations';

function monthShort(bucket: string): string {
    const date = new Date(bucket.length === 7 ? `${bucket}-01` : bucket);
    return Number.isNaN(date.getTime()) ? bucket : date.toLocaleString('en-US', { month: 'short', timeZone: 'UTC' });
}

interface FlatSeatSpend {
    flatMonthly: number;
    seatByConnection: Map<string, number>;
}

/**
 * Forecasts the current month's spend and derives footing (license vs API) and
 * routing (cheaper-model) recommendations. The authoritative metered forecast and
 * routing recommendations come from the bmai-insights nightly snapshot
 * (`CostForecastGateway`); flat seat/subscription cost and footing recommendations
 * are layered on here because bmai-insights has no license-cost access. When the
 * snapshot is unavailable the Phase-1 pure helpers recompute everything client-side
 * from the analytics gateway so the page still works without insights.
 */
@injectable()
export class CostForecastViewModel extends BaseViewModel {
    loading = false;
    error: string | null = null;
    result: ForecastResult | null = null;
    recommendations: Recommendation[] = [];
    /** Forecast takeaways for the rail Insights card (derived + currency-accurate). */
    derivedInsights: TopicCallout[] = [];
    /** Faint prior-month cumulative curves behind the current month, sized by `lookbackMonths`. */
    comparison: MonthComparison[] = [];
    /** How many months (including the current one) the chart shows; drives the look-back control. */
    lookbackMonths = 3;
    /** True when no priced usage covers the month — the forecast reflects only what cost data covers. */
    lowCoverage = false;

    constructor(
        @inject(CostForecastGateway) private readonly forecast: CostForecastGateway,
        @inject(IAnalyticsGatewayToken) private readonly gateway: IAnalyticsGateway,
        @inject(LicensesApiService) private readonly licenses: LicensesApiService,
        @inject(GlobalFilterValueService) private readonly providers: GlobalFilterValueService,
    ) {
        super();
    }

    /** Sets how far back the chart looks (in months, including the current one) and reloads. */
    setLookbackMonths(months: number): void {
        if (months === this.lookbackMonths) return;
        this.lookbackMonths = months;
        void this.reload();
    }

    async reload(): Promise<void> {
        this.loading = true;
        this.error = null;
        this.notifyChange();
        try {
            const snapshot = await this.forecast.getForecast().catch(() => null);
            if (snapshot) {
                await this.applySnapshot(snapshot);
            } else {
                await this.computeClientSide();
            }
            await this.loadComparison();
            this.buildInsights();
        } catch (cause) {
            this.error = cause instanceof Error ? cause.message : String(cause);
        } finally {
            this.loading = false;
            this.notifyChange();
        }
    }

    /** Derives currency-accurate forecast takeaways for the rail Insights card. */
    private buildInsights(): void {
        if (this.result === null) {
            this.derivedInsights = [];
            return;
        }
        const summary = this.result.summary;
        const deltaText = summary.deltaPctVsLastMonth === null ? '' : ` (${summary.deltaPctVsLastMonth >= 0 ? '+' : '−'}${formatPercent(Math.abs(summary.deltaPctVsLastMonth))} vs last month)`;
        const derived: TopicCallout[] = [
            { tone: 'headline', text: `On track to spend ~${formatUsd(summary.projectedMonth)} this month${deltaText}.` },
            { tone: 'opportunity', text: `Running at ~${formatUsd(summary.avgActiveDay)} per active day, ${summary.remainingActiveDays} working day${summary.remainingActiveDays === 1 ? '' : 's'} left.` },
        ];
        if (this.recommendations.length > 0) {
            const totalImpact = this.recommendations.reduce((sum, rec) => sum + rec.monthlyImpactUsd, 0);
            derived.push({ tone: 'opportunity', text: `${this.recommendations.length} recommendation${this.recommendations.length === 1 ? '' : 's'} below could save ~${formatUsd(totalImpact)}/mo.` });
        }
        if (this.lowCoverage) {
            derived.push({ tone: 'watch', text: 'Cost coverage is low this month — the forecast reflects only usage that carries priced cost data.' });
        }
        this.derivedInsights = derived;
    }

    /** Fetches daily spend for the months before the current one and builds the faint comparison curves. */
    private async loadComparison(): Promise<void> {
        if (this.lookbackMonths <= 1) {
            this.comparison = [];
            return;
        }
        const now = new Date();
        const windowStart = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - (this.lookbackMonths - 1), 1));
        const monthStart = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1));
        try {
            const trend = await this.gateway.trend({ source: 'interactions', bucket: 'day', metrics: ['allocatedCostUsd'], start: windowStart.toISOString(), end: monthStart.toISOString() });
            const daily = trend.points.map((point) => ({ date: point.bucket.slice(0, 10), cost: point.metrics.allocatedCostUsd ?? 0 }));
            this.comparison = buildMonthlyComparison(daily, now, this.lookbackMonths);
        } catch {
            this.comparison = [];
        }
    }

    /** Layers frontend-only flat seat cost + footing recommendations onto the authoritative metered snapshot. */
    private async applySnapshot(snapshot: CostForecastSnapshot): Promise<void> {
        const now = new Date();
        const monthStartIso = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1)).toISOString();
        const nowIso = now.toISOString();

        const flat = await this.loadFlatSeatSpend(monthStartIso, nowIso);
        const summary = snapshot.summary;
        const projectedMonth = summary.projectedMetered + flat.flatMonthly;
        const mtdActual = summary.meteredMtd + flat.flatMonthly;
        const lastMonthTotal = summary.lastMonthTotal === null ? null : summary.lastMonthTotal + flat.flatMonthly;
        const deltaVsLastMonth = lastMonthTotal === null ? null : projectedMonth - lastMonthTotal;
        const deltaPctVsLastMonth = lastMonthTotal === null || lastMonthTotal === 0 ? null : (projectedMonth - lastMonthTotal) / lastMonthTotal;

        this.result = {
            summary: {
                meteredMtd: summary.meteredMtd,
                flatMonthly: flat.flatMonthly,
                mtdActual,
                avgActiveDay: summary.avgActiveDay,
                activeDays: summary.activeDays,
                remainingActiveDays: summary.remainingActiveDays,
                projectedMetered: summary.projectedMetered,
                projectedMonth,
                lastMonthTotal,
                deltaVsLastMonth,
                deltaPctVsLastMonth,
            },
            cumulative: snapshot.cumulative.map((point) => ({ date: point.date, actual: point.actual, projected: point.projected })),
            months: snapshot.months.map((bar) => ({ label: bar.label, total: bar.total + flat.flatMonthly, projected: bar.projected })),
        };
        this.lowCoverage = snapshot.lowCoverage;

        const ratio = summary.meteredMtd > 0 ? summary.projectedMetered / summary.meteredMtd : 1;
        const footing = await this.loadFooting(monthStartIso, nowIso, flat.seatByConnection, ratio);
        const routing: Recommendation[] = snapshot.recommendations.map((rec) => ({
            id: rec.id,
            kind: rec.kind,
            tone: rec.tone,
            title: rec.title,
            detail: rec.detail,
            monthlyImpactUsd: rec.monthlyImpactUsd,
        }));
        this.recommendations = [...footing, ...routing];
    }

    /** Sums flat seat/subscription cost records for the month, indexed by provider connection for footing. */
    private async loadFlatSeatSpend(startIso: string, endIso: string): Promise<FlatSeatSpend> {
        const seatByConnection = new Map<string, number>();
        let flatMonthly = 0;
        try {
            const records = await this.licenses.getCostRecords(startIso, endIso);
            for (const record of records) {
                if (!isSubscriptionCost(record)) continue;
                flatMonthly += record.amount;
                const connectionId = record.providerConnectionId;
                if (connectionId) seatByConnection.set(connectionId, (seatByConnection.get(connectionId) ?? 0) + record.amount);
            }
        } catch {
            return { flatMonthly: 0, seatByConnection: new Map() };
        }
        return { flatMonthly, seatByConnection };
    }

    /** Footing recommendations: projected metered spend per provider vs its known seat cost. */
    private async loadFooting(startIso: string, endIso: string, seatByConnection: Map<string, number>, ratio: number): Promise<Recommendation[]> {
        try {
            const providerAgg = await this.gateway.aggregate({ source: 'interactions', groupBy: ['provider_connection_id'], metrics: ['allocatedCostUsd'], start: startIso, end: endIso });
            await this.providers.loadProviders();
            const footingItems: FootingInput[] = providerAgg.rows.map((row) => {
                const connectionId = row.keys.provider_connection_id ?? '';
                return {
                    provider: this.providers.providerLabel(connectionId),
                    meteredMonthly: (row.metrics.allocatedCostUsd ?? 0) * ratio,
                    seatMonthly: seatByConnection.get(connectionId) ?? null,
                };
            });
            return footingRecommendations(footingItems);
        } catch {
            return [];
        }
    }

    /** Fallback: recompute the full forecast + both recommendation kinds client-side when the snapshot is unavailable. */
    private async computeClientSide(): Promise<void> {
        const now = new Date();
        const monthStart = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1));
        const trailingStart = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - 4, 1));
        const monthStartIso = monthStart.toISOString();
        const nowIso = now.toISOString();

        const [dailyTrend, monthlyTrend, providerAgg, modelAgg, modelDiffAgg, records] = await Promise.all([
            this.gateway.trend({ source: 'interactions', bucket: 'day', metrics: ['allocatedCostUsd'], start: monthStartIso, end: nowIso }),
            this.gateway.trend({ source: 'interactions', bucket: 'month', metrics: ['allocatedCostUsd'], start: trailingStart.toISOString(), end: monthStartIso }),
            this.gateway.aggregate({ source: 'interactions', groupBy: ['provider_connection_id'], metrics: ['allocatedCostUsd'], start: monthStartIso, end: nowIso }),
            this.gateway.aggregate({ source: 'interactions', groupBy: ['model'], metrics: ['allocatedCostUsd', 'totalTokens'], start: monthStartIso, end: nowIso }),
            this.gateway.aggregate({ source: 'interactions', groupBy: ['model', 'difficulty'], metrics: ['totalTokens'], start: monthStartIso, end: nowIso }),
            this.licenses.getCostRecords(monthStartIso, nowIso),
        ]);
        await this.providers.loadProviders();

        const seatByConnection = new Map<string, number>();
        let flatMonthly = 0;
        for (const record of records) {
            if (!isSubscriptionCost(record)) continue;
            flatMonthly += record.amount;
            const connectionId = record.providerConnectionId;
            if (connectionId) seatByConnection.set(connectionId, (seatByConnection.get(connectionId) ?? 0) + record.amount);
        }

        const currentMonthDaily = dailyTrend.points.map((point) => ({ date: point.bucket.slice(0, 10), cost: point.metrics.allocatedCostUsd ?? 0 }));
        const trailingMonths = monthlyTrend.points.map((point) => ({ label: monthShort(point.bucket), total: (point.metrics.allocatedCostUsd ?? 0) + flatMonthly }));

        const forecast = computeForecast({ now, currentMonthDaily, flatMonthly, trailingMonths });
        this.result = forecast;
        this.lowCoverage = forecast.summary.meteredMtd <= 0;

        const ratio = forecast.summary.meteredMtd > 0 ? forecast.summary.projectedMetered / forecast.summary.meteredMtd : 1;

        const footingItems: FootingInput[] = providerAgg.rows.map((row) => {
            const connectionId = row.keys.provider_connection_id ?? '';
            return {
                provider: this.providers.providerLabel(connectionId),
                meteredMonthly: (row.metrics.allocatedCostUsd ?? 0) * ratio,
                seatMonthly: seatByConnection.get(connectionId) ?? null,
            };
        });

        const rateByModel = new Map<string, number>();
        for (const row of modelAgg.rows) {
            const model = row.keys.model;
            if (model === undefined || model === '—') continue;
            const tokens = row.metrics.totalTokens ?? 0;
            const cost = row.metrics.allocatedCostUsd ?? 0;
            if (tokens > 0 && cost > 0) rateByModel.set(model, (cost / tokens) * 1_000_000);
        }
        const lightTokensByModel = new Map<string, number>();
        for (const row of modelDiffAgg.rows) {
            const model = row.keys.model;
            if (model === undefined || model === '—') continue;
            const difficulty = (row.keys.difficulty ?? '').toLowerCase();
            if (difficulty !== 'low' && difficulty !== 'medium') continue;
            lightTokensByModel.set(model, (lightTokensByModel.get(model) ?? 0) + (row.metrics.totalTokens ?? 0));
        }
        const modelRouting: ModelRoutingInput[] = [...lightTokensByModel.entries()].map(([model, tokens]) => ({
            model,
            lightMonthlyTokens: tokens * ratio,
            ratePerMTok: rateByModel.get(model) ?? 0,
        }));

        this.recommendations = [...footingRecommendations(footingItems), ...routingRecommendations(modelRouting)];
    }
}
